import { ClipboardUtils } from '../utils/ClipboardUtils'
import { getPublicOrigin } from '../utils/publicOrigin'
import QRCode from 'qrcode'

export class SecretView {
  private secretKey: string
  private onBack: () => void

  constructor(secretKey: string, onBack: () => void) {
    this.secretKey = secretKey
    this.onBack = onBack
  }

  private getSecretPageUrl(): string {
    return `${getPublicOrigin()}/secret/${this.secretKey}`
  }

  public render(): string {
    const secretUrl = this.getSecretPageUrl()
    
    return `
      <div class="bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm rounded-2xl shadow-card border border-slate-100 dark:border-gray-700/90 p-8 sm:p-10 animate-slide-up">
        <!-- Header -->
        <div class="text-center mb-8">
          <div class="w-16 h-16 bg-success-100 dark:bg-success-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg class="w-8 h-8 text-success-600 dark:text-success-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          </div>
          
          <h2 class="text-2xl font-bold text-gray-900 dark:text-white mb-2">Secure Link Created!</h2>
          <p class="text-gray-600 dark:text-gray-300">
            Your link has been generated. Share it securely.
          </p>
        </div>

        <!-- Secret URL Display -->
        <div class="mb-8">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Secret Link
          </label>
          
          <div class="relative">
            <input
              type="text"
              id="secret-url"
              value="${secretUrl}"
              readonly
              class="w-full px-4 py-3 pr-12 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm selection:bg-primary-100"
            />
            <button
              type="button"
              class="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 dark:text-gray-500 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
              data-action="copy-url"
              title="Copy link"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
              </svg>
            </button>
          </div>
        </div>

        <!-- QR Code Section -->
        <div class="mb-8">
          <div class="flex items-center justify-between mb-3">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
              QR Code
            </label>
            <button
              type="button"
              class="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
              data-action="toggle-qr"
            >
              <span class="qr-toggle-text">Show QR Code</span>
            </button>
          </div>
          
          <div id="qr-container" class="hidden">
            <div class="flex justify-center p-6 bg-white dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 rounded-lg">
              <canvas id="qr-code"></canvas>
            </div>
            <p class="text-xs text-gray-500 dark:text-gray-400 text-center mt-2">
              Scan with your mobile device to access the link
            </p>
          </div>
        </div>

        <!-- Security Information -->
        <div class="bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800 rounded-lg p-4 mb-8">
          <div class="flex items-start">
            <svg class="w-5 h-5 text-warning-600 dark:text-warning-400 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z"></path>
            </svg>
            <div class="text-sm">
              <h4 class="font-medium text-warning-800 dark:text-warning-300 mb-2">Important Reminder</h4>
              <ul class="text-warning-700 dark:text-warning-400 space-y-1">
                <li>• <strong>Single view:</strong> The link will be invalidated after first access</li>
                <li>• <strong>Secure sharing:</strong> Use secure channels (Signal, WhatsApp, encrypted email)</li>
                <li>• <strong>Confirmation:</strong> Confirm with the recipient that they received the link</li>
                <li>• <strong>Time limit:</strong> The link will expire as configured</li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="flex flex-col sm:flex-row gap-4">
          <button
            type="button"
            class="flex-1 btn btn-primary"
            data-action="copy-url"
          >
            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
            </svg>
            Copy Link
          </button>
          
          <button
            type="button"
            class="flex-1 btn btn-secondary"
            data-action="share"
          >
            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"></path>
            </svg>
            Share
          </button>
          
          <button
            type="button"
            class="btn btn-outline"
            data-action="back"
          >
            Create Another
          </button>
        </div>
      </div>
    `
  }

  public async bindEvents(container: HTMLElement): Promise<void> {
    const copyBtn = container.querySelectorAll('[data-action="copy-url"]')
    const shareBtn = container.querySelector('[data-action="share"]') as HTMLButtonElement
    const backBtn = container.querySelector('[data-action="back"]') as HTMLButtonElement
    const toggleQrBtn = container.querySelector('[data-action="toggle-qr"]') as HTMLButtonElement

    // Copy URL functionality
    copyBtn.forEach(btn => {
      btn.addEventListener('click', this.handleCopyUrl.bind(this))
    })

    // Share functionality
    shareBtn?.addEventListener('click', this.handleShare.bind(this))

    // Back to form
    backBtn?.addEventListener('click', this.onBack)

    // Toggle QR Code
    toggleQrBtn?.addEventListener('click', this.toggleQrCode.bind(this))

    // Generate QR Code
    await this.generateQrCode()
  }

  private async handleCopyUrl(): Promise<void> {
    const urlInput = document.getElementById('secret-url') as HTMLInputElement
    if (urlInput) {
      try {
        await ClipboardUtils.copyToClipboard(urlInput.value)
        this.showSuccess('Link copied to clipboard!')
        
        // Visual feedback
        urlInput.select()
        setTimeout(() => {
          urlInput.blur()
        }, 1000)
      } catch (error) {
        this.showError('Failed to copy link')
      }
    }
  }

  private async handleShare(): Promise<void> {
    const secretUrl = this.getSecretPageUrl()
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Secure link — Sovran AI',
          text: 'Secure sharing of sensitive information',
          url: secretUrl
        })
      } catch (error) {
        // User canceled or error occurred
        this.fallbackShare(secretUrl)
      }
    } else {
      this.fallbackShare(secretUrl)
    }
  }

  private fallbackShare(url: string): void {
    // Create temporary textarea for mobile sharing
    const textArea = document.createElement('textarea')
    textArea.value = `Secure link: ${url}\n\n⚠️ IMPORTANT: This link can only be accessed once and will expire automatically.`
    document.body.appendChild(textArea)
    textArea.select()
    
    try {
      document.execCommand('copy')
      this.showSuccess('Content copied! Paste it in your messaging app.')
    } catch (error) {
      this.showError('Failed to prepare share')
    }
    
    document.body.removeChild(textArea)
  }

  private async toggleQrCode(): Promise<void> {
    const container = document.getElementById('qr-container')
    const toggleText = document.querySelector('.qr-toggle-text')
    
    if (container && toggleText) {
      const isHidden = container.classList.contains('hidden')
      
      if (isHidden) {
        container.classList.remove('hidden')
        toggleText.textContent = 'Hide QR Code'
        
        // Generate QR code if not already generated
        const canvas = document.getElementById('qr-code') as HTMLCanvasElement
        if (canvas && !canvas.hasAttribute('data-generated')) {
          await this.generateQrCode()
        }
      } else {
        container.classList.add('hidden')
        toggleText.textContent = 'Show QR Code'
      }
    }
  }

  private async generateQrCode(): Promise<void> {
    const canvas = document.getElementById('qr-code') as HTMLCanvasElement
    if (canvas && !canvas.hasAttribute('data-generated')) {
      try {
        const secretUrl = this.getSecretPageUrl()
        
        // Detect dark mode for QR code colors
        const isDarkMode = document.documentElement.classList.contains('dark')
        
        await QRCode.toCanvas(canvas, secretUrl, {
          width: 200,
          margin: 2,
          color: {
            dark: isDarkMode ? '#e2e8f0' : '#0f172a',
            light: isDarkMode ? '#1e293b' : '#ffffff'
          }
        })
        
        canvas.setAttribute('data-generated', 'true')
      } catch (error) {
        console.error('Error generating QR code:', error)
      }
    }
  }

  private showSuccess(message: string): void {
    // This would typically trigger a notification
    console.log('Success:', message)
  }

  private showError(message: string): void {
    // This would typically trigger a notification
    console.error('Error:', message)
  }
} 